import GoogleAnalytics from './vendor/GoogleAnalytics';
import Fivetran from './vendor/Fivetran';
import SWS from './vendor/SWS';
import { snakeCase } from 'lodash';

type AnalyticsVendor = {
  enabled: boolean;
  test: () => boolean;
};

type Dispatch = {
  type: string;
  page: string;
  subject: string;
  modifier: string;
  action: string;
  path: string;
  id: string;
  branchName: string;
  userId: string;
};

class Analytics {
  private adapters: AnalyticsVendor[];
  private identified = false;
  dataLayer = [];

  constructor(platform = 'web') {
    this.adapters = [new GoogleAnalytics(), new Fivetran(platform), new SWS()];
  }

  log(...rest) {
    if (NODE_ENV === 'production') return;
    console &&
      console.info &&
      console.info(
        '%c🦄 Analytics',
        'background: #222; color: #bada55; padding:4px;',
        rest
      );
  }

  isIdentified() {
    return this.identified;
  }

  logout() {
    this.identified = false;
  }

  dispatch(
    { type, page, subject, modifier, action, ...props }: Partial<Dispatch>,
    ...rest
  ) {
    this.log('Dispatch', ...rest);

    const snakedProps = Object.entries(props).reduce(
      (a, b) => ({ ...a, [snakeCase(b[0])]: b[1] }),
      {}
    );

    switch (type) {
      case 'page': {
        this.callAdapters(type, [props.path]);
        break;
      }
      case 'track': {
        const eventName = this.buildEventName(page, subject, modifier, action);
        this.log(type, [eventName, snakedProps]);
        eventName !== null
          ? this.callAdapters(type, [eventName, snakedProps])
          : this.log('Failed to build event name', ...rest);
        break;
      }
      case 'identify': {
        this.identified = this.callAdapters(type, [props.id, snakedProps]);
        break;
      }
      default: {
        console.log('Unknown option:', type);
      }
    }
  }

  buildEventName(page, subject, modifier, action) {
    if (
      typeof page === 'undefined' ||
      typeof subject === 'undefined' ||
      typeof action === 'undefined'
    ) {
      return null;
    }

    return `${snakeCase(page)}.${snakeCase(subject)}${
      modifier ? '.' + snakeCase(modifier) : ''
    }->${snakeCase(action)}`;
  }

  callAdapters(type, args) {
    if (args.length < 1) {
      this.log(
        `aborted calling analytics adapters, event ${type}, has no arguments`
      );
      return false;
    }

    this.adapters.forEach((adapter) => {
      if (NODE_ENV !== 'production') {
        /** */
        console.log(' ⭐️ DEV MODE ⭐️');
        this.log(
          `Pushed ${NODE_ENV} analytics event to ${adapter.constructor.name} via DEV MODE`,
          type,
          args
        );
      } else {
        if (
          adapter.enabled &&
          typeof adapter.test === 'function' &&
          adapter.test()
        ) {
          adapter[type] && adapter[type](...args);
          this.log(
            `Pushed ${NODE_ENV} analytics event to ${adapter.constructor.name}`,
            type,
            args
          );
        } else {
          this.log(
            `Failed to push event to ${adapter.constructor.name}, have you included the vendor snippet?`,
            type,
            args
          );
        }
      }
    });

    return true;
  }
}

export default Analytics;
